/**
 * Editor dos ativos do ticker do mercado: tipo, código como a API DE DADOS o
 * entrega e o rótulo que o site mostra. A ordem da lista é a ordem do ticker.
 */
import { Button } from '@/components/atoms';
import type { AtivoTicker, ErroDeCampo, TipoAtivo } from '@/types/portal';

import { CampoSelecao, CampoTexto } from './campos';
import { erroDe, mover, remover, trocar } from './helpers';
import styles from './Portal.module.css';

interface Props {
  ativos: AtivoTicker[];
  onChange: (ativos: AtivoTicker[]) => void;
  erros: ErroDeCampo[];
  campo: string;
}

const TIPOS: { valor: TipoAtivo; rotulo: string }[] = [
  { valor: 'indice', rotulo: 'Índice ou ação' },
  { valor: 'cambio', rotulo: 'Câmbio' },
];

export function EditorAtivos({ ativos, onChange, erros, campo }: Props) {
  const erroLista = erroDe(erros, campo);
  return (
    <div>
      <h4 className={styles.sub2}>Ativos do ticker ({ativos.length})</h4>
      {ativos.length === 0 ? (
        <p className={styles.vazio}>Nenhum ativo. O ticker e os cartões de mercado ficam vazios.</p>
      ) : (
        <ol className={styles.lista} aria-label="Ativos do ticker">
          {ativos.map((a, i) => (
            <li key={i} className={styles.linha}>
              <div className={styles.linhaCampos}>
                <CampoSelecao
                  rotulo={`Tipo do ativo ${i + 1}`}
                  valor={a.tipo}
                  opcoes={TIPOS}
                  onChange={(tipo) => onChange(trocar(ativos, i, { ...a, tipo }))}
                />
                <CampoTexto
                  rotulo={`Código do ativo ${i + 1}`}
                  valor={a.ativo}
                  placeholder={a.tipo === 'cambio' ? 'USD-BRL' : 'IBOVESPA'}
                  maxLength={40}
                  mono
                  erro={erroDe(erros, `${campo}.${i}.ativo`)}
                  onChange={(ativo) => onChange(trocar(ativos, i, { ...a, ativo }))}
                />
                <CampoTexto
                  rotulo={`Rótulo do ativo ${i + 1}`}
                  valor={a.rotulo}
                  placeholder="Ibovespa"
                  maxLength={30}
                  erro={erroDe(erros, `${campo}.${i}.rotulo`)}
                  onChange={(rotulo) => onChange(trocar(ativos, i, { ...a, rotulo }))}
                />
              </div>
              <div className={styles.linhaAcoes}>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={i === 0}
                  onClick={() => onChange(mover(ativos, i, i - 1))}
                  aria-label={`Subir ativo ${i + 1}`}
                >
                  Subir
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={i === ativos.length - 1}
                  onClick={() => onChange(mover(ativos, i, i + 1))}
                  aria-label={`Descer ativo ${i + 1}`}
                >
                  Descer
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange(remover(ativos, i))}
                  aria-label={`Remover ativo ${i + 1}`}
                >
                  Remover
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}
      {erroLista && <span className={styles.erroCampo}>{erroLista}</span>}
      <div style={{ marginTop: 'var(--sp-2)' }}>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={ativos.length >= 30}
          onClick={() => onChange([...ativos, { tipo: 'indice', ativo: '', rotulo: '' }])}
        >
          Adicionar ativo
        </Button>
      </div>
    </div>
  );
}
